(() => {
  'use strict';
  const initialized = new WeakSet();
  const select = (root, selector) => root.querySelector(selector);
  const all = (root, selector) => [...root.querySelectorAll(selector)];

  function initializeProduct(root) {
    if (initialized.has(root)) return;
    const configElement = select(root, '[data-pal-product-config]');
    if (!configElement) return;
    let config;
    try { config = JSON.parse(configElement.textContent); } catch { return; }
    initialized.add(root);
    const strings = config.strings;
    const product = config.product;
    let variant = product.variants.find(v => String(v.id) === String(config.selectedVariant));
    const currencyLocale = config.locale?.includes('-') ? config.locale : `${config.locale || 'de'}-CH`;
    const formatter = new Intl.NumberFormat(currencyLocale, {style: 'currency', currency: config.currency || 'CHF'});
    const money = cents => formatter.format(cents / 100);
    const quantityInput = () => select(root, '[data-quantity-input][name="quantity"]');
    const quantity = () => Math.max(1, Number(quantityInput()?.value) || 1);
    const maxQuantity = () => Math.min(999, Number(quantityInput()?.max) || 999);
    const quantityIsValid = () => { const input = quantityInput(); return !input || (input.checkValidity() && Number.isInteger(Number(input.value)) && Number(input.value) >= 1 && Number(input.value) <= maxQuantity()); };
    let calculated = 0;
    let adding = false;

    function totals() {
      const panelTotal = (variant?.price || 0) * quantity();
      const extras = all(root, '[data-pal-extra]:checked:not(:disabled)').reduce((sum, el) => sum + Number(el.dataset.price), 0);
      for (const el of all(root, '[data-pal-payment-total], [data-pal-project-total]')) el.textContent = money(panelTotal + extras);
      const projectButton = select(root, '[data-pal-add-project]');
      if (projectButton) projectButton.disabled = !variant?.available || adding || !quantityIsValid();
      const stickyPrice = select(root, '[data-pal-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = money(panelTotal);
      const stickyButton = select(root, '[data-pal-sticky-add]');
      if (stickyButton) { stickyButton.disabled = !variant?.available; stickyButton.textContent = variant?.available ? strings.addToCart : strings.soldOut; }
    }

    const height = Number(config.panel.height);
    const width = Number(config.panel.width);
    const dimensions = select(root, '[data-pal-dimensions]');
    const area = select(root, '[data-pal-area]');
    if (dimensions && width > 0 && height > 0) dimensions.textContent = `${height} × ${width} cm`;
    if (area && width > 0 && height > 0) area.textContent = `${new Intl.NumberFormat(config.locale, {maximumFractionDigits: 3}).format(width * height / 10000)} m²`;

    function calculate() {
      const output = select(root, '[data-pal-calculation]');
      const apply = select(root, '[data-pal-apply-calculation]');
      if (!output || !apply) return;
      const wallWidth = Number(select(root, '[data-pal-wall-width]')?.value);
      const wallHeight = Number(select(root, '[data-pal-wall-height]')?.value);
      const valid = [wallWidth, wallHeight].every(n => Number.isFinite(n) && n > 0 && n <= 5000);
      calculated = valid && width > 0 && height > 0 ? Math.ceil(wallWidth / width) * Math.ceil(wallHeight / height) : 0;
      output.textContent = width <= 0 || height <= 0 ? strings.missingDimensions : calculated ? `${calculated} ${strings.panels} · ${money(calculated * (variant?.price || 0))}` : strings.enter;
      const limited = calculated > maxQuantity();
      apply.disabled = !calculated || limited || !variant?.available;
      select(root, '[data-pal-calc-note]').textContent = limited ? strings.quantityLimit : wallHeight > height && calculated ? `${strings.joints} ${strings.note}` : strings.note;
      const tallLink = select(root, '[data-pal-tall-wall]');
      if (tallLink) tallLink.hidden = !(calculated && height < 300 && wallHeight > height && wallHeight <= 300);
    }

    const params = new URL(location.href).searchParams;
    if (params.get('palissar_selection') === 'fallback') {
      const status = select(root, '[data-pal-selection-status]');
      if (status) status.textContent = strings.fallback;
    }
    for (const link of all(root, '[data-pal-range]')) {
      let candidates;
      try { candidates = JSON.parse(link.dataset.palRangeProducts); } catch { continue; }
      const exact = candidates.filter(p => p.finish === config.panel.finish && p.felt === config.panel.felt && p.available);
      const match = exact.find(p => p.height === config.panel.height) || exact[0];
      const target = match || candidates.find(p => p.available) || candidates[0];
      if (target) {
        const url = new URL(target.url, location.origin);
        if (!match && link.getAttribute('aria-current') !== 'true') url.searchParams.set('palissar_selection', 'fallback');
        link.href = url.href;
      }
    }

    root.addEventListener('theme:variant:change', event => {
      variant = event.detail?.variant || null;
      totals(); calculate();
    });
    root.addEventListener('change', event => {
      if (event.target.matches('[data-pal-extra-variant]')) {
        const option = event.target.selectedOptions[0];
        const checkbox = select(event.target.closest('.pal-accessory-card'), '[data-pal-extra]');
        checkbox.checked = false;
        checkbox.dataset.variantId = option.value;
        checkbox.dataset.price = option.dataset.price;
        checkbox.disabled = option.dataset.available !== 'true';
      }
      if (event.target.matches('[data-pal-wall-width], [data-pal-wall-height]')) calculate();
      totals();
    });
    root.addEventListener('input', event => {
      if (event.target.matches('[data-pal-wall-width], [data-pal-wall-height]')) calculate();
      if (event.target.matches('[data-quantity-input]')) totals();
    });
    root.addEventListener('click', async event => {
      if (event.target.closest('[data-pal-sticky-add]')) select(root, 'form[data-product-form] [data-add-to-cart]')?.click();
      const openButton = event.target.closest('[data-pal-payment-open]');
      if (openButton) {
        totals();
        const dialog = document.getElementById(openButton.getAttribute('aria-controls'));
        if (dialog && !dialog.open) { dialog.showModal(); dialog.addEventListener('close', () => openButton.focus(), {once: true}); }
      }
      const closeButton = event.target.closest('[data-pal-close]');
      if (closeButton) closeButton.closest('dialog').close();
      if (event.target.matches('dialog') && event.target.open) {
        const r = event.target.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) event.target.close();
      }
      if (event.target.closest('[data-increase-quantity], [data-decrease-quantity]')) queueMicrotask(totals);
      if (event.target.closest('[data-pal-apply-calculation]') && calculated > 0 && calculated <= maxQuantity()) {
        const input = quantityInput();
        if (input) { input.value = String(calculated); input.dispatchEvent(new Event('change', {bubbles: true})); }
      }
      const button = event.target.closest('[data-pal-add-project]');
      if (!button || adding || !variant?.available || !quantityIsValid()) return;
      const status = select(root, '[data-pal-cart-status]');
      adding = true; totals(); status.textContent = strings.adding;
      const items = [{id: variant.id, quantity: quantity()}];
      for (const checkbox of all(root, '[data-pal-extra]:checked:not(:disabled)')) items.push({id: Number(checkbox.dataset.variantId), quantity: 1});
      // Native selling-plan selection and line-item properties follow the panel line.
      const form = select(root, 'form[data-product-form]');
      if (form) {
        const data = new FormData(form);
        const sellingPlan = data.get('selling_plan');
        if (sellingPlan) items[0].selling_plan = Number(sellingPlan);
        const properties = {};
        for (const [name, value] of data) {
          const match = name.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        }
        if (Object.keys(properties).length) items[0].properties = properties;
      }
      try {
        const response = await fetch(`${config.root}cart/add.js`, {method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify({items})});
        const added = await response.json();
        if (!response.ok) throw new Error(added.description || added.message || strings.error);
        status.textContent = `${strings.added} `;
        const cartLink = document.createElement('a'); cartLink.href = config.cartUrl; cartLink.textContent = strings.viewCart; status.append(cartLink);
        // Do not retry an add after a successful response; a cart refresh failure must not duplicate items.
        document.dispatchEvent(new CustomEvent('theme:cart:reload', {bubbles: true}));
        fetch(`${config.root}cart.js`, {headers: {'Accept': 'application/json'}}).then(r => { if (!r.ok) throw new Error(); return r.json(); }).then(cart => document.dispatchEvent(new CustomEvent('theme:cart:change', {detail: {cart}, bubbles: true}))).catch(() => {});
        all(root, '[data-pal-extra]').forEach(el => { el.checked = false; });
      } catch (error) { status.textContent = error.message || strings.error; }
      finally { adding = false; totals(); }
    });
    const sticky = select(root, '[data-pal-sticky]');
    const nativeAdd = select(root, 'form[data-product-form] [data-add-to-cart]');
    if (sticky && nativeAdd && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => { sticky.hidden = entries[0].isIntersecting || entries[0].boundingClientRect.top > 0; }, {threshold: 0});
      observer.observe(nativeAdd);
      root.addEventListener('shopify:section:unload', () => observer.disconnect(), {once: true});
    }
    totals(); calculate();
  }

  function initialize(scope = document) {
    for (const root of all(scope, '[data-pal-product]')) initializeProduct(root);
    for (const root of all(scope, '[data-pal-home]')) {
      if (initialized.has(root)) continue;
      initialized.add(root);
      root.addEventListener('click', event => {
        const openButton = event.target.closest('[data-pal-payment-open]');
        if (openButton) {
          const dialog = document.getElementById(openButton.getAttribute('aria-controls'));
          if (dialog && !dialog.open) {
            dialog.showModal();
            dialog.addEventListener('close', () => openButton.focus(), {once: true});
          }
        }
        if (event.target.closest('[data-pal-close]')) event.target.closest('dialog').close();
        if (event.target.matches('dialog') && event.target.open) {
          const rect = event.target.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
        }
      });
    }
    for (const root of all(scope, '[data-pal-professional]')) {
      if (initialized.has(root)) continue;
      initialized.add(root);
      const form = select(root, '.pro-form');
      if (!form) continue;
      root.addEventListener('click', event => {
        const request = event.target.closest('[data-request]');
        const partner = event.target.closest('[data-partner]');
        const finish = event.target.closest('[data-finish]');
        if (request && form.elements['contact[interesse]']) form.elements['contact[interesse]'].value = request.dataset.request;
        if (partner && form.elements['contact[partnertyp]']) form.elements['contact[partnertyp]'].value = partner.dataset.partner;
        if (finish && form.elements['contact[furnier]']) {
          all(root, '[data-finish]').forEach(el => el.setAttribute('aria-pressed', String(el === finish)));
          form.elements['contact[furnier]'].value = finish.dataset.finish;
          const status = select(root, '#pro-finish-status'); if (status) status.textContent = finish.dataset.finish;
        }
      });
      // Shopify owns submission, validation errors, CAPTCHA and successful-post rendering.
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize()); else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
